/**
 * Analytics constants for the doula_patient_details flow (PROD-2076), posted
 * through api.event() to POST v1/events/. The backend resolves the funnel step
 * from the exact event string and stores an unknown string with an empty step,
 * so these are a contract — never reword them.
 */
export const PATIENT_DETAILS_FLOW = 'doula_patient_details';

export const OPENED_PATIENT_EVENT = 'opened patient';

/** Keyed by the BP graph period length in days (the PERIODS values in bp-graphs). */
export const CHANGED_GRAPH_PERIOD_EVENTS: Record<number, string> = {
  7: 'changed graph period - 1 week',
  14: 'changed graph period - 2 weeks',
  30: 'changed graph period - 1 month',
};

/** The tap that opens the thresholds popover in the BP card (bp-thresholds-popover). */
export const OPENED_THRESHOLD_GUIDELINES_EVENT = 'opened threshold guidelines';

/** The tap that opens the dialer, not the call — whether a call was placed cannot be known. */
export const CALL_PATIENT_EVENT = 'call patient';

/**
 * The doula_community_search flow (PROD-2234): what a doula did next from search results, each
 * posted with metadata `{ search_id }` only. The search itself is reported to
 * v1/community/searches/, never as an event (the events table holds no free text). Same contract
 * rule as above — never reword them.
 */
export const SEARCH_FLOW = 'doula_community_search';

/** A doula row in the results, or a result post's author: her profile opens. */
export const OPENED_DOULA_PROFILE_EVENT = 'opened doula profile';

/** The "Read more" tap on a result post; the automatic open-up of a match below the cut is not one. */
export const OPENED_UP_POST_EVENT = 'opened up post';

/** A like on a result post, once the server has saved it; an unlike is not one. */
export const LIKED_POST_EVENT = 'liked post';

/** A comment on a result post, once the server has saved it. */
export const COMMENTED_ON_POST_EVENT = 'commented on post';

/** The Message button on the profile of a doula found by search: it only opens the chat. */
export const TAPPED_MESSAGE_EVENT = 'tapped message';

/** PROD-2281: the closing tap of the patient overlay (back arrow or dialog close). */
export const CLOSED_PATIENT_EVENT = 'closed patient';
export const OPENED_CASE_STUDY_OVERVIEW_EVENT = 'opened case study overview';

/**
 * The rest of PROD-2281: doula actions that leave no trace on the backend — fetch-only opens,
 * edits that overwrite the previous value, and taps that never reach it (share sheet, dialer,
 * external links). Same contract rule as above. Items 11, 12, 12b and the DM push click were
 * registered in PROD-2088 and must stay byte-exact.
 */
export const NAVIGATION_FLOW = 'doula_app_navigation';

export const INVITES_FLOW = 'doula_invites';
/** The "Send invite" tap that opens the share sheet; whether she shared cannot be known. */
export const SHARED_INVITE_EVENT = 'shared invite';
export const RESENT_INVITE_EVENT = 'resent invite';
export const OPENED_CASE_STUDY_DISCLAIMER_EVENT = 'opened case study disclaimer';

export const CHAT_FLOW = 'doula_chat';
export const DECLINED_CHAT_CONSENT_EVENT = 'declined chat consent';
/** A link in a Megi answer; posted with `{ url }`. */
export const OPENED_CHAT_LINK_EVENT = 'opened chat link';
export const EXPANDED_CHAT_DISCLAIMER_EVENT = 'expanded chat disclaimer';
export const RETRIED_CHAT_MESSAGE_EVENT = 'retried chat message';

/** The inbox opened by navigation (NAVIGATION_FLOW); a DM push open posts DM_NOTIFICATION_CLICKED_EVENT. */
export const OPENED_MESSAGES_EVENT = 'opened messages';
/**
 * PATIENT_DETAILS_FLOW, with `{ patient_id }`: a patient thread tapped in the inbox. Not sent when the
 * patient screen opens (its DM panel is always there, so 'opened patient' says it). Doula-to-doula
 * threads post nothing.
 */
export const OPENED_PATIENT_THREAD_EVENT = 'opened patient thread';
/** PATIENT_DETAILS_FLOW, with `{ patient_id }`, once the server has saved the message. */
export const SENT_MESSAGE_TO_PATIENT_EVENT = 'sent message to patient';
/** A link in a DM (NAVIGATION_FLOW): no URL, the conversation is private. */
export const OPENED_MESSAGE_LINK_EVENT = 'opened message link';
export const RETRIED_LOADING_PATIENT_THREAD_EVENT = 'retried loading patient thread';
export const RETRIED_LOADING_MESSAGES_EVENT = 'retried loading messages';
export const RETRIED_LOADING_MESSAGE_THREAD_EVENT = 'retried loading message thread';
export const RETRIED_LOADING_THREAD_PROFILE_EVENT = 'retried loading thread profile';

/** A DM push tap without a notification id (old shell, browser push). */
export const DIRECT_MESSAGE_FLOW = 'direct_message';
export const DM_NOTIFICATION_CLICKED_EVENT = 'direct message notification clicked';

/** Community feed actions. In search results the SEARCH_FLOW event is posted instead where one exists. */
export const COMMUNITY_FLOW = 'doula_community';
/** With `{ post_id, url }`. */
export const OPENED_POST_LINK_EVENT = 'opened post link';
export const EXPANDED_POST_EVENT = 'expanded post';
export const OPENED_COMMENTS_EVENT = 'opened comments';
/** The Message button on another doula's profile, with `{ profile_id }`; it only opens the chat. */
export const MESSAGE_DOULA_EVENT = 'message doula';
export const OPENED_CREATE_POST_EVENT = 'opened create post';
export const CANCELLED_CREATE_POST_EVENT = 'cancelled create post';
/** After the PATCH succeeds, with `{ post_id }`. */
export const EDITED_POST_EVENT = 'edited post';

export const PROFILE_FLOW = 'doula_profile';
/** After the save succeeds, with `{ field }`. */
export const EDITED_PROFILE_EVENT = 'edited profile';

export const ONBOARDING_FLOW = 'onboarding_mobile_app_doulas_v1';

/** Onboarding legal links (ONBOARDING_FLOW) and the side-nav link (NAVIGATION_FLOW). */
export const OPENED_TERMS_AND_CONDITIONS_EVENT = 'opened terms and conditions';
export const OPENED_PRIVACY_POLICY_EVENT = 'opened privacy policy';
/** Sent before the token is cleared, or it would post unauthenticated. */
export const LOGGED_OUT_EVENT = 'logged out';
